import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  Search, 
  Filter,
  Eye,
  Download,
  Plus,
  FileText
} from 'lucide-react';
import { receiptsAPI, clientsAPI } from '../services/api';

// Custom Receipt Icon with Rupee symbol
const ReceiptIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1-2-1Z" />
    <path d="M14 8H8" />
    <path d="M16 12H8" />
    <path d="M13 16H8" />
    <text x="15.5" y="9.5" fontSize="7" fontWeight="bold" fill="currentColor" textAnchor="middle">₹</text>
  </svg>
);

interface Client {
  _id: string;
  clientName: string;
  email: string;
  contactNumber: string;
  address: {
    street: string;
    city: string;
    state: string;
    pincode: string;
    country: string;
  };
  gstin?: string;
  placeOfSupply: string;
}

interface ReceiptData {
  _id: string;
  receiptNumber: string;
  receiptDate: string;
  invoice?: {
    _id: string;
    invoiceNumber: string;
    invoiceDate: string;
  };
  invoiceNumber: string;
  client: Client;
  amount: number;
  amountInWords: string;
  paymentMode: string;
  paymentDetails: {
    referenceNumber?: string;
    bankName?: string;
    chequeNumber?: string;
    transactionDate?: string;
  };
  notes?: string;
  isManual: boolean;
  createdAt: string;
  tdsDeducted?: number;
  tdsPercentage?: number;
}

const AllReceipts: React.FC = () => {
  const [receipts, setReceipts] = useState<ReceiptData[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClient, setSelectedClient] = useState<string>('');
  const [generatingPDF, setGeneratingPDF] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchReceipts();
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, selectedClient]);

  const fetchData = async () => {
    try {
      await Promise.all([
        fetchReceipts(),
        fetchClients()
      ]);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchReceipts = async () => {
    try {
      const params: any = {
        limit: 1000,
        search: searchTerm || undefined
      };
      
      if (selectedClient) {
        params.clientId = selectedClient;
      }

      const response = await receiptsAPI.getAll(params);
      setReceipts(response.data.receipts || []);
    } catch (error) {
      console.error('Error fetching receipts:', error);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await clientsAPI.getAll({ limit: 1000 });
      setClients(response.data.clients || []);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
    }).format(amount);
  };

  const getPaymentModeColor = (mode: string) => {
    const colors: { [key: string]: string } = {
      'Cash': 'bg-green-100 text-green-800',
      'Cheque': 'bg-blue-100 text-blue-800',
      'Bank Transfer': 'bg-purple-100 text-purple-800',
      'UPI': 'bg-orange-100 text-orange-800',
      'Card': 'bg-pink-100 text-pink-800',
      'Other': 'bg-gray-100 text-gray-800'
    };
    return colors[mode] || colors['Other'];
  };

  const handleView = (receiptId: string) => {
    navigate(`/receipt/${receiptId}`);
  };

  const filteredReceipts = receipts;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <ReceiptIcon className="h-8 w-8 text-primary-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">All Receipts</h1>
            <p className="text-gray-600">View and manage all your receipts</p>
          </div>
        </div>
        <button
          onClick={() => navigate('/receipt/manual')}
          className="btn-primary flex items-center space-x-2"
        >
          <Plus className="h-5 w-5" />
          <span>Create Receipt</span>
        </button>
      </div>

      {/* Filters */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="card"
      >
        <div className="flex flex-wrap items-center gap-4">
          {/* Search */}
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search receipts..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>

          {/* Client Filter */}
          <div className="flex items-center space-x-2">
            <Filter className="h-5 w-5 text-gray-500" />
            <select
              value={selectedClient}
              onChange={(e) => setSelectedClient(e.target.value)}
              className="border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">All Clients</option>
              {clients.map((client) => (
                <option key={client._id} value={client._id}>
                  {client.clientName}
                </option>
              ))}
            </select>
          </div>
        </div>
      </motion.div>

      {/* Receipts List */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="card"
      >
        {filteredReceipts.length === 0 ? (
          <div className="text-center py-8">
            <ReceiptIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500 mb-4">No receipts found</p>
            {filteredReceipts.length !== receipts.length && (
              <button
                onClick={() => {
                  setSearchTerm('');
                  setSelectedClient('');
                }}
                className="text-primary-600 hover:text-primary-700 font-medium"
              >
                Clear filters
              </button>
            )}
            {receipts.length === 0 && (
              <button
                onClick={() => navigate('/receipt/manual')}
                className="btn-primary mt-4 inline-flex items-center space-x-2"
              >
                <Plus className="h-4 w-4" />
                <span>Create your first receipt</span>
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="table-header">Receipt No.</th>
                  <th className="table-header">Invoice No.</th>
                  <th className="table-header">Client</th>
                  <th className="table-header">Date</th>
                  <th className="table-header">Gross Amount</th>
                  <th className="table-header">Net Amount</th>
                  <th className="table-header">Payment Mode</th>
                  <th className="table-header">Type</th>
                  <th className="table-header">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredReceipts.map((receipt) => {
                  // Round off net amount
                  const netAmount = Math.round(receipt.tdsDeducted ? receipt.amount - receipt.tdsDeducted : receipt.amount);
                  return (
                    <tr key={receipt._id} className="hover:bg-gray-50">
                      <td className="table-cell font-medium">
                        {receipt.receiptNumber}
                      </td>
                      <td className="table-cell">
                        {receipt.invoiceNumber}
                      </td>
                      <td className="table-cell">
                        <div>
                          <p className="font-medium text-gray-900">{receipt.client.clientName}</p>
                          <p className="text-sm text-gray-500">{receipt.client.email}</p>
                        </div>
                      </td>
                      <td className="table-cell">
                        {new Date(receipt.receiptDate).toLocaleDateString('en-IN')}
                      </td>
                      <td className="table-cell">
                        <div>
                          <p className="font-medium text-gray-900">{formatCurrency(receipt.amount)}</p>
                          {receipt.tdsDeducted && receipt.tdsDeducted > 0 && (
                            <p className="text-xs text-gray-500">TDS: {formatCurrency(receipt.tdsDeducted)}</p>
                          )}
                        </div>
                      </td>
                      <td className="table-cell font-medium text-primary-600">
                        {formatCurrency(netAmount)}
                      </td>
                      <td className="table-cell">
                        <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${getPaymentModeColor(receipt.paymentMode)}`}>
                          {receipt.paymentMode}
                        </span>
                      </td>
                      <td className="table-cell">
                        {receipt.isManual ? (
                          <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                            Manual
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                            From Invoice
                          </span>
                        )}
                      </td>
                      <td className="table-cell">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleView(receipt._id)}
                            className="text-primary-600 hover:text-primary-700 p-2 hover:bg-primary-50 rounded-lg transition-colors"
                            title="View receipt"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleView(receipt._id)}
                            disabled={generatingPDF === receipt._id}
                            className="text-green-600 hover:text-green-700 p-2 hover:bg-green-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Download PDF (opens receipt page)"
                          >
                            <Download className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default AllReceipts;

